/**
 * The birth ring — a small zodiac wheel, drawn the way a chart is drawn.
 *
 * The Ascendant sits on the left horizon and longitude runs counter-clockwise
 * from it, so a body's place on the ring is where it actually was in the sky
 * over the birthplace: a Sun that rose two hours ago is just above the horizon
 * on the left, a Moon in the fourth house is down at the bottom. That is why it
 * is the one element that travels from the birth screen to the biography to the
 * Studio header — it is the same fact, drawn, every time.
 */

const RING_BODIES = ['sun', 'moon', 'ascendant']
export const GLYPH = { sun: '☉', moon: '☾', ascendant: '↑' }

/** Longitudes of the three bodies the ring draws, from either payload shape. */
export function ringBodies (placements) {
  const out = {}
  if (!placements) return out
  const entries = Array.isArray(placements)
    ? placements.map(p => [p.body, p])
    : Object.entries(placements)
  for (const [key, p] of entries) {
    if (RING_BODIES.includes(key) && typeof p?.longitude === 'number') out[key] = p.longitude
  }
  return out
}

/**
 * Geometry for an SVG of `size` px. Angles are measured from the Ascendant,
 * counter-clockwise, with 0 at 9 o'clock; no Ascendant means Aries on the left.
 */
export function ringLayout (longitudes, size) {
  const lons = longitudes || {}
  const cx = size / 2
  const cy = size / 2
  const r = size / 2 - Math.max(6, size * 0.08)
  const asc = typeof lons.ascendant === 'number' ? lons.ascendant : 0
  const at = (lon, radius) => {
    const th = ((lon - asc) * Math.PI) / 180
    return { x: cx - radius * Math.cos(th), y: cy + radius * Math.sin(th) }
  }
  const inner = r * 0.82
  const ticks = []
  for (let i = 0; i < 12; i++) {
    const a = at(i * 30, r)
    const b = at(i * 30, inner)
    ticks.push({ x1: a.x, y1: a.y, x2: b.x, y2: b.y })
  }
  const bodies = RING_BODIES
    .filter(k => typeof lons[k] === 'number')
    .map(k => ({ key: k, glyph: GLYPH[k], ...at(lons[k], r * 0.91) }))
  return {
    cx, cy, r, inner, ticks, bodies,
    horizon: { x1: cx - r, y1: cy, x2: cx + r, y2: cy },
    glyphSize: Math.max(9, size * 0.11),
  }
}
